/* Adapts the desktop order summary subscription into compact records for the browser mobile Orders screen. */
import {useEffect, useState} from 'react';
import {
  OrderSide,
  OrderState,
  OrderType,
  type OrderRecord,
  type SingleAlgoFullReport,
  type TraderDBAccount,
  type TradingAccount,
} from '@qep/types/src';

export type MobileOrderSummaryTone = 'open' | 'partial' | 'filled' | 'cancelled' | 'rejected' | 'pending';

export type MobileOrderSummaryRecord = {
  accountLabel: string;
  filledQuantity: string;
  id: string;
  orderQuantity: string;
  orderTypeLabel: string;
  priceLabel: string;
  remainingQuantity: string;
  sideLabel: string;
  sortTime: number;
  statusLabel: string;
  statusTone: MobileOrderSummaryTone;
  strategyLabel?: string;
  symbolLabel: string;
  timeLabel: string;
  traderLabel: string;
};

export type MobileOrderSummaryState = {
  errorMessage?: string;
  orders: MobileOrderSummaryRecord[];
};

type OrderSummarySubscriptionUpdate = {
  payload?: MobileOrderSummarySourceRecord | MobileOrderSummarySourceRecord[];
  topic?: string;
};

type OrderSummarySubscribe = (
  callback: (error: unknown, update?: OrderSummarySubscriptionUpdate) => void,
  detailedSource?: string,
) => (() => void) | void;

type MobileOrderSummaryApi = {
  subscribeToOrderSummary?: OrderSummarySubscribe;
};

type MobileOrderSummaryGlobal = typeof globalThis & {
  QEP?: {
    apiServices?: {
      orderSummary?: MobileOrderSummaryApi;
    };
  };
  window?: {
    QEP?: {
      apiServices?: {
        orderSummary?: MobileOrderSummaryApi;
      };
    };
  };
};

type MobileManualSummarySourceRecord = OrderRecord & {
  orderPx?: number | string;
  orderQty?: number | string;
  price?: number | string;
  side?: string;
  strategy?: string;
  totalFillQty?: number | string;
};

type MobileAlgoSummarySourceRecord = SingleAlgoFullReport & {
  key?: string;
  orderId?: string;
};

type MobileOrderSummarySourceRecord = MobileManualSummarySourceRecord | MobileAlgoSummarySourceRecord;

const notAvailableLabel = 'Unknown';
const unknownTraderLabel = 'Unknown trader';
const replacingTopics = new Set(['INITIAL', 'RESET']);
const statusDisplayByValue = new Map<unknown, {label: string; tone: MobileOrderSummaryTone}>([
  [OrderState.ORDERSTATE_PARTIAL_FILLED, {label: 'Partial', tone: 'partial'}],
  [OrderState.ORDERSTATE_PENDING_REPLACE, {label: 'Partial', tone: 'partial'}],
  [OrderState.ORDERSTATE_CALCULATED, {label: 'Partial', tone: 'partial'}],
  [OrderState.ORDERSTATE_FILLED, {label: 'Filled', tone: 'filled'}],
  [OrderState.ORDERSTATE_REJECTED, {label: 'Rejected', tone: 'rejected'}],
  [OrderState.ORDERSTATE_CANCEL_REJECTED, {label: 'Rejected', tone: 'rejected'}],
  [OrderState.ORDERSTATE_PENDING_NEW, {label: 'Pending New', tone: 'pending'}],
  [OrderState.ORDERSTATE_ACKNOWLEDGED, {label: 'Pending New', tone: 'pending'}],
  [OrderState.ORDERSTATE_PENDING_CANCEL, {label: 'Pending Cancel', tone: 'cancelled'}],
  [OrderState.ORDERSTATE_PAUSED, {label: 'Paused', tone: 'cancelled'}],
  [OrderState.ORDERSTATE_NEW, {label: 'Open', tone: 'open'}],
]);

/** Converts unknown service errors into copy that fits the compact mobile Orders status row. */
function getOrderSummaryErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  return 'Order summary is unavailable.';
}

/** Reads the order summary API from the QEP global without importing desktop-only app shell code. */
export function getMobileOrderSummaryApi(source = globalThis as MobileOrderSummaryGlobal): MobileOrderSummaryApi | undefined {
  return source.QEP?.apiServices?.orderSummary ?? source.window?.QEP?.apiServices?.orderSummary;
}

/** Returns a subscription payload as an array so INITIAL, RESET, and UPDATE records share one reducer path. */
function getOrderSummaryPayloadRecords(update: OrderSummarySubscriptionUpdate | undefined): MobileOrderSummarySourceRecord[] {
  if (!update?.payload) {
    return [];
  }

  return Array.isArray(update.payload) ? update.payload : [update.payload];
}

/** Parses numeric service values while tolerating the stringified quantities used by order summary payloads. */
function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = Number(value.replaceAll(',', '').trim());
  return Number.isFinite(normalized) ? normalized : undefined;
}

/** Returns a trimmed display string so mobile rows do not render empty or symbolic placeholders. */
function toDisplayLabel(value: unknown, fallback = notAvailableLabel): string {
  if (typeof value !== 'string') {
    return fallback;
  }

  const trimmedValue = value.trim();
  return trimmedValue || fallback;
}

/** Normalizes enum payloads that may arrive as strings from order summary subscriptions. */
function normalizeServiceEnumValue(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const numericValue = Number(trimmedValue);
  return Number.isFinite(numericValue) ? numericValue : trimmedValue;
}

/** Formats a quantity for dense mobile cards without trailing decimals when the value is whole. */
function formatQuantity(value: unknown, fallback = '0'): string {
  const numericValue = toFiniteNumber(value);
  if (numericValue === undefined) {
    return fallback;
  }

  return Number.isInteger(numericValue) ? String(numericValue) : String(numericValue);
}

/** Formats a nullable price, returning MKT when a market order or missing price has no display value. */
function formatPrice(record: MobileManualSummarySourceRecord): string {
  const price = record.orderPx ?? record.orderPrice ?? record.targetOrderPrice ?? record.price;
  const numericPrice = toFiniteNumber(price);

  if (numericPrice === undefined) {
    return normalizeServiceEnumValue(record.orderType) === OrderType.ORDERTYPE_LIMIT ? notAvailableLabel : 'MKT';
  }

  return String(numericPrice);
}

/** Resolves the account label across raw service records and normalized order summary grid records. */
function getAccountLabel(account: OrderRecord['account']): string {
  if (!account) {
    return notAvailableLabel;
  }

  if (typeof account === 'string') {
    return toDisplayLabel(account);
  }

  const tradingAccount = account as Partial<TradingAccount & TraderDBAccount>;
  return toDisplayLabel(tradingAccount.code ?? tradingAccount.accountCode ?? tradingAccount.id, notAvailableLabel);
}

/** Picks the shortest recognizable symbol label from raw product records and normalized order summary rows. */
function getSymbolLabel(record: MobileManualSummarySourceRecord): string {
  const recordWithStringSymbol = record as MobileManualSummarySourceRecord & {symbol?: string | OrderRecord['symbol']};
  if (typeof recordWithStringSymbol.symbol === 'string') {
    return toDisplayLabel(recordWithStringSymbol.symbol);
  }

  return recordWithStringSymbol.symbol?.symbol
    ?? record.exchangeSymbol
    ?? record.fixSymbol
    ?? record.qepSymbol
    ?? record.root
    ?? notAvailableLabel;
}

/** Reads the symbol and MIC fields used to disambiguate spread-leg order summary rows. */
function getSpreadLegIdentitySymbol(record: MobileManualSummarySourceRecord): {segmentMic: string; symbol: string} {
  if (!record.symbol || typeof record.symbol !== 'object') {
    return {segmentMic: '', symbol: ''};
  }

  const symbol = record.symbol as {segmentMic?: unknown; symbol?: unknown};
  return {
    segmentMic: typeof symbol.segmentMic === 'string' ? symbol.segmentMic : '',
    symbol: typeof symbol.symbol === 'string' ? symbol.symbol : '',
  };
}

/** Builds a stable manual order row id that matches replay keying for spread legs sharing one order id. */
function getManualOrderSummaryId(record: MobileManualSummarySourceRecord): string | undefined {
  if (
    typeof record.orderId === 'string'
    && record.orderId.length > 0
    && Number(normalizeServiceEnumValue(record.multiLegReportingType)) === 2
  ) {
    const {segmentMic, symbol} = getSpreadLegIdentitySymbol(record);
    return `spread:${record.orderId}:${symbol}:${segmentMic}:${record.orderSide ?? ''}`;
  }

  return record.orderId ?? record.clientOrderId ?? record.key ?? record.orderRecordKey;
}

/** Converts raw order-side enum, numeric-string, and text variants into a concise mobile side label. */
function normalizeSideLabel(value: unknown): string | undefined {
  const normalizedValue = normalizeServiceEnumValue(value);
  if (normalizedValue === OrderSide.ORDERSIDE_BUY) {
    return 'Buy';
  }

  if (normalizedValue === OrderSide.ORDERSIDE_SELL) {
    return 'Sell';
  }

  if (typeof normalizedValue !== 'string') {
    return undefined;
  }

  const normalizedText = normalizedValue.trim().toLowerCase();
  if (['-', 'n/a', 'na', 'unknown'].includes(normalizedText)) {
    return undefined;
  }

  if (normalizedText === 'buy' || normalizedText === 'b') {
    return 'Buy';
  }

  if (normalizedText === 'sell' || normalizedText === 's') {
    return 'Sell';
  }

  return normalizedValue.trim();
}

/** Converts order side enum/string values into human-readable Buy/Sell labels for mobile cards. */
function getSideLabel(record: MobileManualSummarySourceRecord): string {
  return normalizeSideLabel(record.side)
    ?? normalizeSideLabel(record.orderSide)
    ?? normalizeSideLabel(record.orderSideString)
    ?? notAvailableLabel;
}

/** Converts order type enum/string values into the concise labels expected in the mobile summary card. */
function getOrderTypeLabel(orderType: OrderRecord['orderType']): string {
  const normalizedOrderType = normalizeServiceEnumValue(orderType);
  if (normalizedOrderType === OrderType.ORDERTYPE_LIMIT) {
    return 'Limit';
  }

  if (normalizedOrderType === OrderType.ORDERTYPE_MARKET) {
    return 'Market';
  }

  if (normalizedOrderType === OrderType.ORDERTYPE_STOP) {
    return 'Stop';
  }

  if (normalizedOrderType === OrderType.ORDERTYPE_STOP_LIMIT) {
    return 'Stop Limit';
  }

  if (typeof normalizedOrderType === 'string' || typeof normalizedOrderType === 'number') {
    return String(normalizedOrderType);
  }

  return notAvailableLabel;
}

/** Resolves trader identity from the variants sent by manual and normalized order summary payloads. */
function getTraderLabel(record: MobileManualSummarySourceRecord): string {
  const {traderInfo} = record;
  if (typeof traderInfo === 'string') {
    return toDisplayLabel(traderInfo, unknownTraderLabel);
  }

  if (traderInfo && typeof traderInfo === 'object') {
    const typedTraderInfo = traderInfo as {name?: string; traderId?: string; username?: string};
    return typedTraderInfo.name ?? typedTraderInfo.username ?? typedTraderInfo.traderId ?? unknownTraderLabel;
  }

  return toDisplayLabel(record.trader ?? record.traderId ?? record.trader_id, unknownTraderLabel);
}

/** Converts text status strings into status labels and tones while avoiding raw numeric fallbacks. */
function getTextStatusDisplay(status: string): {label: string; tone: MobileOrderSummaryTone} | undefined {
  const normalizedStatus = status.trim().toLowerCase();
  if (!normalizedStatus || ['-', 'n/a', 'na', 'unknown'].includes(normalizedStatus)) {
    return undefined;
  }

  if (normalizedStatus.includes('reject')) {
    return {label: status, tone: 'rejected'};
  }

  if (normalizedStatus.includes('cancel') || normalizedStatus.includes('expire') || normalizedStatus.includes('pause') || normalizedStatus.includes('stop')) {
    return {label: status, tone: 'cancelled'};
  }

  if (normalizedStatus.includes('partial')) {
    return {label: status, tone: 'partial'};
  }

  if (normalizedStatus.includes('fill')) {
    return {label: status, tone: 'filled'};
  }

  if (normalizedStatus.includes('pending') || normalizedStatus.includes('ack')) {
    return {label: status, tone: 'pending'};
  }

  if (normalizedStatus === 'new' || normalizedStatus === 'open') {
    return {label: status, tone: 'open'};
  }

  return {label: status, tone: 'open'};
}

/** Converts order state enum/string values into concise labels and color tones for summary chips. */
function getStatusDisplay(record: MobileManualSummarySourceRecord): {label: string; tone: MobileOrderSummaryTone} {
  const status = normalizeServiceEnumValue(record.status ?? record.orderStatus ?? record.statusString);

  if (status === OrderState.ORDERSTATE_CANCELED || status === OrderState.ORDERSTATE_EXPIRED) {
    return {label: toFiniteNumber(record.totalFillQty ?? record.totalFilled) ? 'Canceled Partial' : 'Canceled', tone: 'cancelled'};
  }

  const knownStatusDisplay = statusDisplayByValue.get(status);
  if (knownStatusDisplay) {
    return knownStatusDisplay;
  }

  if (typeof status === 'string') {
    return getTextStatusDisplay(status) ?? {label: notAvailableLabel, tone: 'open'};
  }

  if (status === undefined || status === null) {
    return {label: notAvailableLabel, tone: 'open'};
  }

  return {label: notAvailableLabel, tone: 'open'};
}

/** Parses the most useful timestamp on an order summary record for display and newest-first sorting. */
function getOrderTimestamp(record: MobileManualSummarySourceRecord): {label: string; sortTime: number} {
  const timestamp = record.transactionTime
    ?? record.sendingTimeUTC
    ?? record.createdAt
    ?? record.initialOrderTime
    ?? record.lastUpdateTime;
  const parsedTime = timestamp ? new Date(timestamp).getTime() : Number.NaN;

  if (!Number.isFinite(parsedTime)) {
    return {label: notAvailableLabel, sortTime: 0};
  }

  return {
    label: new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(parsedTime)),
    sortTime: parsedTime,
  };
}

/** Detects algo reports so mobile can display them without the desktop order summary grid converter. */
function isAlgoSummaryRecord(record: MobileOrderSummarySourceRecord): record is MobileAlgoSummarySourceRecord {
  return 'algoRunId' in record && typeof record.algoRunId === 'string';
}

/** Sums an algo-trade numeric field while tolerating missing or stringified upstream values. */
function sumAlgoTradeQuantity(
  record: MobileAlgoSummarySourceRecord,
  field: 'remainingQuantity' | 'targetQuantity' | 'tradedQuantity',
): number {
  return record.algoTrades.reduce((total, trade) => total + (toFiniteNumber(trade[field]) ?? 0), 0);
}

/** Builds a compact symbol label for algo records that can contain one or many underlying trades. */
function getAlgoSymbolLabel(record: MobileAlgoSummarySourceRecord): string {
  const explicitSymbols = record.symbolsWithQTY ?? record.symbols;
  if (Array.isArray(explicitSymbols) && explicitSymbols.length > 0) {
    const firstSymbol = explicitSymbols[0];
    return typeof firstSymbol === 'string' ? toDisplayLabel(firstSymbol) : firstSymbol?.symbol ?? notAvailableLabel;
  }

  return record.algoTrades[0]?.symbol?.symbol ?? record.algoTrades[0]?.qepSymbol ?? notAvailableLabel;
}

/** Converts algo summary records into one compact row keyed by algo run id. */
function toMobileAlgoSummaryRecord(record: MobileAlgoSummarySourceRecord): MobileOrderSummaryRecord {
  const targetQuantity = sumAlgoTradeQuantity(record, 'targetQuantity');
  const filledQuantity = record.algoMetrics?.tradedQuantityOverall ?? sumAlgoTradeQuantity(record, 'tradedQuantity');
  const remainingQuantity = sumAlgoTradeQuantity(record, 'remainingQuantity');
  const timestamp = getOrderTimestamp({
    lastUpdateTime: record.lastUpdated ? new Date(record.lastUpdated) : undefined,
    transactionTime: record.latestStatusDate ? new Date(record.latestStatusDate) : undefined,
  });

  return {
    accountLabel: record.accountInfo?.executionCode ?? record.accountInfo?.clearingAccount ?? notAvailableLabel,
    filledQuantity: formatQuantity(filledQuantity),
    id: record.orderId ?? record.key ?? record.algoRunId,
    orderQuantity: formatQuantity(record.algoMetrics?.targetQuantityOverall ?? targetQuantity),
    orderTypeLabel: record.algoType || 'Algo',
    priceLabel: notAvailableLabel,
    remainingQuantity: formatQuantity(remainingQuantity),
    sideLabel: 'Algo',
    sortTime: timestamp.sortTime,
    statusLabel: record.algoStatus || 'Algo',
    statusTone: record.completedDate ? 'filled' : 'open',
    symbolLabel: getAlgoSymbolLabel(record),
    timeLabel: timestamp.label,
    traderLabel: record.accountInfo?.traderId ?? unknownTraderLabel,
  };
}

/** Converts raw or normalized order summary records into the compact row model used by mobile. */
export function toMobileOrderSummaryRecord(record: MobileOrderSummarySourceRecord): MobileOrderSummaryRecord | undefined {
  if (isAlgoSummaryRecord(record)) {
    return toMobileAlgoSummaryRecord(record);
  }

  const id = getManualOrderSummaryId(record);
  if (!id) {
    return undefined;
  }

  const orderQuantity = record.orderQty ?? record.orderQuantity ?? record.quantity;
  const filledQuantity = record.totalFillQty ?? record.totalFilled ?? record.fillQuantity;
  const orderQuantityNumber = toFiniteNumber(orderQuantity);
  const filledQuantityNumber = toFiniteNumber(filledQuantity) ?? 0;
  const remainingQuantity = record.remainingQty ?? (
    orderQuantityNumber === undefined ? undefined : Math.max(orderQuantityNumber - filledQuantityNumber, 0)
  );
  const status = getStatusDisplay(record);
  const timestamp = getOrderTimestamp(record);

  return {
    accountLabel: getAccountLabel(record.account),
    filledQuantity: formatQuantity(filledQuantity),
    id,
    orderQuantity: formatQuantity(orderQuantity),
    orderTypeLabel: getOrderTypeLabel(record.orderType),
    priceLabel: formatPrice(record),
    remainingQuantity: formatQuantity(remainingQuantity),
    sideLabel: getSideLabel(record),
    sortTime: timestamp.sortTime,
    statusLabel: status.label,
    statusTone: status.tone,
    strategyLabel: record.strategy ?? record.executionTag,
    symbolLabel: getSymbolLabel(record),
    timeLabel: timestamp.label,
    traderLabel: getTraderLabel(record),
  };
}

/** Sorts order summary cards newest first, with order id as a deterministic tie breaker for tests and replay. */
export function sortMobileOrderSummaryOrders(orders: MobileOrderSummaryRecord[]): MobileOrderSummaryRecord[] {
  return [...orders].sort((left, right) => right.sortTime - left.sortTime || right.id.localeCompare(left.id));
}

/** Applies INITIAL/RESET replacement and UPDATE upsert semantics from the order summary subscription. */
export function reduceMobileOrderSummaryOrders(
  previousOrders: MobileOrderSummaryRecord[],
  update: OrderSummarySubscriptionUpdate | undefined,
): MobileOrderSummaryRecord[] {
  const nextOrders = getOrderSummaryPayloadRecords(update)
    .map(record => toMobileOrderSummaryRecord(record))
    .filter((record): record is MobileOrderSummaryRecord => record !== undefined);

  if (!update?.topic || replacingTopics.has(update.topic)) {
    return sortMobileOrderSummaryOrders(nextOrders);
  }

  const byId = new Map(previousOrders.map(order => [order.id, order]));
  for (const order of nextOrders) {
    byId.set(order.id, order);
  }

  return sortMobileOrderSummaryOrders([...byId.values()]);
}

/** Subscribes the mobile shell to replayed order summary data and cleans up when the shell unmounts. */
export function useMobileOrderSummary(): MobileOrderSummaryState {
  const [state, setState] = useState<MobileOrderSummaryState>({orders: []});

  useEffect(() => {
    const subscribeToOrderSummary = getMobileOrderSummaryApi()?.subscribeToOrderSummary;
    if (!subscribeToOrderSummary) {
      setState({
        errorMessage: 'Order summary is unavailable.',
        orders: [],
      });
      return undefined;
    }

    let isMounted = true;
    const unsubscribe = subscribeToOrderSummary((error, update) => {
      if (!isMounted) {
        return;
      }

      if (error) {
        setState(previous => ({
          ...previous,
          errorMessage: getOrderSummaryErrorMessage(error),
        }));
        return;
      }

      setState(previous => ({
        errorMessage: undefined,
        orders: reduceMobileOrderSummaryOrders(previous.orders, update),
      }));
    }, 'Browser Mobile Orders');

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, []);

  return state;
}
